/**
 * Resolution + parsing for the runtime controls config. Precedence:
 * creator override (settings, via the injected config) > adapter
 * recommendation > `DEFAULT_SCHEME_ID`. Parsing is tolerant — the shell must
 * boot cleanly no matter what the injected tag contains.
 */
import { CONTROL_SCHEMES, DEFAULT_SCHEME_ID, isControlSchemeId } from './catalog.js';
import { mergedBindings } from './keys.js';
import type {
  ControlSchemeChoice,
  ControlSchemeDef,
  ControlsConfig,
  KeyBindings,
  ResolvedScheme,
  SchemeParams,
  SemanticKey,
} from './types.js';

export const SEMANTIC_KEYS: readonly SemanticKey[] = ['up', 'down', 'left', 'right', 'action'];

/** Overlay sparse params onto the def's fallbacks; unknown keys/values drop. */
function denseParams(def: ControlSchemeDef, sparse: SchemeParams | undefined): SchemeParams {
  const out: Record<string, string> = {};
  for (const p of def.params) {
    const v = sparse?.[p.key];
    out[p.key] = p.options.some((o) => o.id === v) ? (v as string) : p.fallback;
  }
  return out;
}

/** Densify a choice against the catalog. */
export function resolveScheme(
  override: ControlSchemeChoice | null,
  recommended: ControlSchemeChoice | null,
): ResolvedScheme {
  const choice = override ?? recommended ?? { scheme: DEFAULT_SCHEME_ID };
  const def = CONTROL_SCHEMES[choice.scheme];
  return { id: def.id, def, params: denseParams(def, choice.params) };
}

function parseChoice(raw: unknown): ControlSchemeChoice | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const scheme = (raw as { scheme?: unknown }).scheme;
  if (!isControlSchemeId(scheme)) return null;
  const rawParams = (raw as { params?: unknown }).params;
  const params: Record<string, string> = {};
  if (typeof rawParams === 'object' && rawParams !== null) {
    for (const [k, v] of Object.entries(rawParams)) if (typeof v === 'string') params[k] = v;
  }
  return { scheme, params };
}

function parseKeys(raw: unknown): Partial<KeyBindings> | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const out: Partial<Record<SemanticKey, readonly string[]>> = {};
  for (const key of SEMANTIC_KEYS) {
    const v = (raw as Record<string, unknown>)[key];
    if (Array.isArray(v) && v.length > 0 && v.every((c) => typeof c === 'string' && c !== '')) {
      out[key] = v as string[];
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Parse the injected controls-config JSON. Returns `null` for anything that
 * isn't a well-formed v1 config; unknown scheme ids, malformed params, and
 * malformed key lists degrade to "no override" rather than throwing.
 */
export function parseControlsConfig(raw: unknown): ControlsConfig | null {
  if (typeof raw !== 'object' || raw === null) return null;
  if ((raw as { v?: unknown }).v !== 1) return null;
  const scheme = parseChoice((raw as { scheme?: unknown }).scheme);
  const keys = parseKeys((raw as { keys?: unknown }).keys);
  return { v: 1, ...(scheme ? { scheme } : {}), ...(keys ? { keys } : {}) };
}

/** Key codes controls may never claim (the shell's pause/menu bindings). */
export const RESERVED_KEY_CODES: readonly string[] = ['Escape', 'KeyP'];

/** The bindable `KeyboardEvent.code` shapes — exactly the set every consumer
 *  (keycap labels, Phaser capture names, the studio capture editor) can
 *  handle, so a stored binding can never be half-supported. */
const BINDABLE_CODE = /^(Key[A-Z]|Digit[0-9]|Arrow(Up|Down|Left|Right)|Space|Enter|Tab|ShiftLeft|ShiftRight|ControlLeft|ControlRight)$/;

/**
 * Validate a creator's key remap (server-side gate for `updateGameSettings`
 * and the studio editor's inline check). Returns a list of human-readable
 * problems; empty = valid.
 */
export function validateKeyBindings(keys: Partial<KeyBindings>): string[] {
  const problems: string[] = [];
  const seen = new Map<string, SemanticKey>();
  for (const key of SEMANTIC_KEYS) {
    const codes = keys[key];
    if (codes === undefined) continue;
    if (!Array.isArray(codes)) {
      problems.push(`${key}: must be a list of key codes`);
      continue;
    }
    if (codes.length === 0) problems.push(`${key}: at least one key is required`);
    for (const code of codes) {
      if (typeof code !== 'string' || !BINDABLE_CODE.test(code)) {
        problems.push(`${key}: "${String(code)}" is not a bindable key`);
        continue;
      }
      if (RESERVED_KEY_CODES.includes(code)) {
        problems.push(`${key}: ${code} is reserved for pause`);
        continue;
      }
      const other = seen.get(code);
      if (other !== undefined && other !== key) {
        problems.push(`${code} is bound to both ${other} and ${key}`);
      }
      seen.set(code, key);
    }
  }
  if (problems.length > 0) return problems;

  // Conflicts must hold on the EFFECTIVE bindings the shell runs (overrides
  // overlaid on the defaults) — remapping only `action` to KeyW must flag the
  // clash with the DEFAULT up=KeyW, not pass because the sparse map is clean.
  const effective = mergedBindings(keys);
  const effectiveSeen = new Map<string, SemanticKey>();
  for (const key of SEMANTIC_KEYS) {
    for (const code of effective[key]) {
      const other = effectiveSeen.get(code);
      if (other !== undefined && other !== key) {
        problems.push(`${code} is bound to both ${other} and ${key} — remap ${other} too`);
      }
      effectiveSeen.set(code, key);
    }
  }
  return problems;
}
